var _excluded = ["color", "fullWidth", "label", "helperText", "helperTextPlacement", "disabled", "required", "options", "outlined", "edges", "showError", "size", "errorMessage", "columns", "name"];
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import React from 'react';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormGroup from '@material-ui/core/FormGroup';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import CheckBoxOutlinedIcon from '@material-ui/icons/CheckBoxOutlined';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import FormControl from '../FormControl';
var useStyles = makeStyles(function (theme) {
  return {
    checkbox: {
      marginRight: theme.spacing(6),
      '& .MuiTypography-body1': {
        fontSize: function fontSize(_ref) {
          var size = _ref.size;
          return size === 'large' ? theme.typography.size.medium : theme.typography.size.small;
        }
      }
    },
    outlined: {
      border: function border(_ref2) {
        var color = _ref2.color;
        return "2px solid " + (theme.palette[color] ? theme.palette[color].main : theme.palette.primary.main);
      },
      padding: "" + theme.spacing(1, 3)
    }
  };
});
var CheckboxGroup = function CheckboxGroup(_ref3) {
  var color = _ref3.color,
    fullWidth = _ref3.fullWidth,
    label = _ref3.label,
    helperText = _ref3.helperText,
    helperTextPlacement = _ref3.helperTextPlacement,
    disabled = _ref3.disabled,
    required = _ref3.required,
    options = _ref3.options,
    outlined = _ref3.outlined,
    edges = _ref3.edges,
    showError = _ref3.showError,
    size = _ref3.size,
    errorMessage = _ref3.errorMessage,
    columns = _ref3.columns,
    name = _ref3.name,
    rest = _objectWithoutPropertiesLoose(_ref3, _excluded);
  var classes = useStyles({
    size: size,
    color: color
  });
  return /*#__PURE__*/React.createElement(FormControl, {
    name: name,
    fullWidth: fullWidth,
    label: size === 'large' ? /*#__PURE__*/React.createElement(Typography, {
      variant: "h4",
      color: "textPrimary",
      component: "span"
    }, label) : label,
    helperText: helperText,
    helperTextPlacement: helperTextPlacement,
    disabled: disabled,
    required: required,
    showError: showError,
    errorMessage: errorMessage,
    edges: edges
  }, /*#__PURE__*/React.createElement(FormGroup, {
    row: true,
    className: outlined ? classes.outlined : null
  }, /*#__PURE__*/React.createElement(Grid, {
    container: true
  }, /*#__PURE__*/React.createElement("input", {
    name: name,
    style: {
      appearance: 'none',
      border: 'none',
      outline: 'none',
      margin: 0,
      padding: 0,
      // needs to be 'visisble for iOS to scroll to..
      width: '100%',
      height: 1,
      marginBottom: -1,
      opacity: 0.1,
      // prevent zoom...
      fontSize: 20,
      position: 'absolute',
      bottom: 0,
      left: 0
    }
    // prevent keyboard
    ,
    readOnly: true
  }), options && options.map(function (option) {
    return /*#__PURE__*/React.createElement(Grid, {
      item: true,
      xs: columns > 0 ? parseInt(12 / columns, 10) : null,
      key: option.label
    }, /*#__PURE__*/React.createElement(FormControlLabel, {
      className: classes.checkbox,
      label: option.label,
      control: /*#__PURE__*/React.createElement(Checkbox, _extends({
        color: "default",
        name: option.name,
        checked: option.checked,
        disabled: disabled,
        icon: size === 'large' ? /*#__PURE__*/React.createElement(RadioButtonUncheckedIcon, {
          fontSize: "large",
          color: color
        }) : /*#__PURE__*/React.createElement(CheckBoxOutlineBlankIcon, null),
        checkedIcon: size === 'large' ? /*#__PURE__*/React.createElement(CheckCircleOutlineIcon, {
          fontSize: "large",
          color: color
        }) : /*#__PURE__*/React.createElement(CheckBoxOutlinedIcon, null)
      }, option.checkboxProps, rest))
    }));
  }))));
};
CheckboxGroup.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * If `true`, the `input` element will be disabled.
   */
  color: PropTypes.oneOf(['primary', 'secondary']),
  /**
   * If `true`, the `input` element will be disabled.
   */
  disabled: PropTypes.bool,
  /**
   * If `true`, the input will take up the full width of its container.
   */
  fullWidth: PropTypes.bool,
  /**
   * Number of columns to split the check boxes into
   */
  columns: PropTypes.number,
  /**
   * The helper text content.
   */
  helperText: PropTypes.string,
  /**
   * Field name
   */
  name: PropTypes.string.isRequired,
  /**
   * Placed the helper text above or below the input.
   */
  helperTextPlacement: PropTypes.oneOf(['above', 'below']),
  /**
   * The label content.
   */
  label: PropTypes.string,
  /**
   * The options for this field
   */
  options: PropTypes.arrayOf(PropTypes.shape({
    checked: PropTypes.bool,
    label: PropTypes.node,
    name: PropTypes.string,
    checkboxProps: PropTypes.object
  })).isRequired,
  /**
   * If `true`, the label is displayed as required and the `input` element` will be required.
   */
  required: PropTypes.bool,
  /**
   * The size of the checkbox.
   */
  size: PropTypes.oneOf(['large', 'medium']),
  /**
   * If `true`, the group is displayed with a outlined style.
   */
  outlined: PropTypes.bool,
  /**
   * Whether or not to display an error message (if there is a message to display).
   */
  showError: PropTypes.bool,
  /**
   * The error message to display if showError === true
   */
  errorMessage: PropTypes.string,
  /**
   * Make button edges rounded or square
   */
  edges: PropTypes.oneOf(['normal', 'rounded', 'sharp'])
} : {};
CheckboxGroup.defaultProps = {
  color: 'primary',
  disabled: false,
  fullWidth: false,
  helperText: '',
  helperTextPlacement: 'above',
  label: '',
  required: false,
  outlined: false,
  showError: false,
  errorMessage: '',
  edges: 'normal',
  size: 'medium',
  columns: 0
};
export default CheckboxGroup;